import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config
    if (error.response?.status === 401 && !original?._retry && !original?.url?.includes('/auth/refresh')) {
      original._retry = true
      try {
        await api.post('/auth/refresh')
        return api(original)
      } catch {
        if (!original?.url?.includes('/me')) {
          window.location.href = '/'
        }
      }
    }
    return Promise.reject(error)
  }
)

export const authApi = {
  googleStartUrl: () => `${API_BASE_URL}/auth/google/start`,
  me: () => api.get<User>('/me'),
  refresh: () => api.post('/auth/refresh'),
  logout: () => api.post('/auth/logout'),
}

export const documentsApi = {
  playgroundExtract: (file: File, limit = 7) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('limit', String(limit))
    return api.post<DocumentPreview>('/playground/extract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  upload: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post<UploadResponse>('/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  uploadBatch: (files: File[]) => {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    return api.post<BatchUploadResponse>('/documents/batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  list: () => api.get<Document[]>('/documents'),
  get: (id: string) => api.get<Document>(`/documents/${id}`),
  fileUrl: (id: string) => `${API_BASE_URL}/documents/${id}/file`,
  delete: (id: string) => api.delete(`/documents/${id}`),
  deleteBatch: (ids: string[]) => api.post<BatchDeleteResponse>('/documents/batch/delete', { document_ids: ids }),
  startProcessing: (id: string) => api.post<ProcessResponse>(`/documents/${id}/process`),
  startProcessingBatch: (ids: string[]) => api.post<BatchProcessResponse>('/documents/batch/process', { document_ids: ids }),
  preview: (id: string, limit?: number) => api.get<DocumentPreview>(`/documents/${id}/preview`, { params: limit ? { limit } : undefined }),
  export: (id: string, selectedColumnIds: string[], format: 'xlsx' | 'csv') =>
    api.post<ExportResponse>(`/documents/${id}/exports`, {
      selected_column_ids: selectedColumnIds,
      format,
    }),
}

export const advancedApi = {
  getHighlights: (id: string) => api.get<HighlightsResponse>(`/documents/${id}/highlights`),
  getUsage: () => api.get<UsageResponse>('/usage'),
  runChandraScan: (id: string, pageRange = '1') => api.post<ChandraScanResponse>(`/documents/${id}/chandra-scan`, null, { params: { page_range: pageRange } }),
  enableAdvancedView: (id: string) => api.post<AdvancedViewResponse>(`/documents/${id}/advanced-view`),
  getChandraResult: (id: string) => api.get<ChandraResultResponse>(`/documents/${id}/chandra-result`),
}

export const paymentApi = {
  getCheckoutUrl: (productId: string) => api.post<{ checkout_url: string }>('/payments/checkout', { product_id: productId }),
}

export const jobsApi = {
  get: (id: string) => api.get<ProcessingJob>(`/jobs/${id}`),
  eventsUrl: (id: string) => `${API_BASE_URL}/jobs/${id}/events`,
}

export interface User {
  id: string
  email: string
  full_name?: string
  avatar_url?: string
  role: 'user' | 'admin'
}

export type DocumentStatus =
  | 'uploaded'
  | 'duplicate'
  | 'queued'
  | 'processing'
  | 'processed'
  | 'exporting'
  | 'export_ready'
  | 'failed'
  | 'deleted'

export interface Document {
  id: string
  user_id: string
  original_filename: string
  file_type: 'pdf'
  file_size: number
  sha256: string
  status: DocumentStatus
  duplicate_of_id?: string | null
  error_message?: string | null
  page_count: number
  row_count: number
  column_count: number
  uploaded_at: string
  processing_started_at?: string | null
  processed_at?: string | null
  exported_at?: string | null
  created_at: string
  latest_job?: ProcessingJob
}

export interface UploadResponse {
  document: Document
  duplicate_of?: Document | null
}

export interface BatchUploadResponse {
  documents: UploadResponse[]
  errors: Array<{ filename?: string; error: string; upgrade_required?: boolean }>
  count: number
}

export interface ProcessingJob {
  id: string
  rq_job_id?: string
  document_id: string
  status: 'queued' | 'running' | 'completed' | 'failed' | 'canceled'
  progress: number
  current_step?: string
  pages_processed: number
  tables_detected: number
  rows_extracted: number
  error_message?: string | null
  created_at: string
  started_at?: string | null
  completed_at?: string | null
}

export interface ProcessResponse {
  document: Document
  job: ProcessingJob
}

export interface BatchProcessResponse {
  documents: ProcessResponse[]
  errors: Array<{ document_id: string; error: string; status?: number }>
  count: number
}

export interface BatchDeleteResponse {
  deleted_ids: string[]
  missing_ids: string[]
  count: number
}

export interface JobLog {
  id: number
  job_id: string
  level: 'info' | 'warning' | 'error'
  message: string
  payload: Record<string, any>
  created_at: string
}

export interface DetectedColumn {
  id: string
  column_key: string
  column_name: string
  column_index: number
  table_id?: string
  data_type: 'text' | 'numeric' | 'date' | 'currency'
  confidence_score: number
  is_arabic: boolean
  sample_values: string[]
}

export interface PreviewTable {
  id: string
  page: number
  table_index: number
  header_rows: string[][]
  columns: string[]
  rows: string[][]
  row_count: number
  column_count: number
}

export interface DocumentPreview {
  document: Document
  profile: string
  confidence: number
  quality_report: {
    profile: string
    confidence: number
    row_count: number
    column_count: number
    missing_rows?: number
    sum_previous?: number
    sum_annual?: number
    sum_total?: number
    skipped_metadata_tables?: number
    skipped_tables?: number
    warnings?: string[]
  }
  active_schema: {
    key: string
    columns: DetectedColumn[]
    row_count: number
    column_count: number
  }
  columns: DetectedColumn[]
  tables: PreviewTable[]
  sample_table?: PreviewTable | null
  row_count: number
  column_count: number
  page_count: number
}

export interface ExportResponse {
  export: {
    id: string
    document_id: string
    format: 'xlsx' | 'csv'
    filename: string
    file_size: number
    columns_exported: number
    download_url: string
    created_at: string
  }
  document: Document
}

export interface DashboardStats {
  total_documents: number
  processing_jobs: number
  completed_jobs: number
  storage_used_bytes: number
}

// New interfaces for subscription and advanced features
export interface UsageResponse {
  user_id: string
  plan_type: 'free' | 'pro' | 'advanced'
  pdfs: {
    used: number
    remaining: number | -1
    limit: number | -1
  }
  pages: {
    processed: number
    limit: number | -1
  }
  exports: {
    count: number
    limit: number | -1
  }
  chandra: {
    used: number
    remaining: number | -1
    limit: number | -1
  }
  is_pro: boolean
  is_advanced: boolean
}

export interface HighlightsResponse {
  document_id: string
  highlights: Array<{
    id: string
    page_number: number
    bbox: {
      x0: number
      y0: number
      x1: number
      y1: number
    }
    confidence: number
    metadata: Record<string, any>
  }>
  page_count: number
}

export interface ChandraScanResponse {
  document: Document
  extraction: {
    method: 'chandra'
    tables: number
    columns: number
    rows: number
    confidence: number
  }
  chandra_quota: {
    used: number
    remaining: number | -1
  }
}

export interface AdvancedViewResponse {
  document_id: string
  advanced_view_enabled: boolean
  highlights_count: number
  plan_type: string
}

export interface ChandraResultResponse {
  document_id: string
  result: {
    id: string
    output_md: string
    output_html: string
    output_json: Record<string, any>
    model_version: string
    inference_method: string
    processing_time_ms: number
    page_metadata: Array<{
      page: number
      tables: Array<{ bbox?: string; content?: string }>
      images: Array<{ bbox?: string }>
    }>
    confidence_score: number
    tables_detected: number
    images_detected: number
    status: string
    error_message?: string
    created_at: string
  }
}

export default api
